import { db } from '../db/db.js';
import type {
  BatchAddedPayload,
  BatchConsumedPayload,
  BatchDiscardedPayload,
  BatchOpenedPayload,
  BatchQuantityAdjustedPayload,
  BatchUpdatedPayload,
  InventoryEvent,
  ItemCreatedPayload,
  ItemDiscardedPayload,
  ItemMinQuantitySetPayload,
  SettingsUpdatedPayload,
  ShoppingAddedPayload,
  ShoppingReceivedPayload,
  ShoppingStatusChangedPayload,
  SubscriptionAdvancedPayload,
  SubscriptionCreatedPayload,
  SubscriptionDeactivatedPayload,
  SubscriptionUpdatedPayload,
} from './types.js';
import type { Item, ShoppingItem, Subscription, UserSettings } from '../db/schema.js';
import { DEFAULT_USER_SETTINGS } from '../db/schema.js';

/**
 * Apply a single event's effect to projected tables (ADR-013).
 *
 * Idempotent within a transaction: callers should wrap in db.transaction
 * to keep events table writes and projection writes atomic. The dispatcher
 * already does this; rebuildProjections also wraps.
 *
 * v0.4：Settings projection 已加入（settings_updated event）— 為 sync 鋪路。
 * 直接 db.settings.put 仍允許作為過渡相容（rebuildProjections 重建會被覆蓋）。
 */
export async function applyEventToProjections(event: InventoryEvent): Promise<void> {
  switch (event.type) {
    case 'item_created':
      await applyItemCreated(event.payload as ItemCreatedPayload);
      return;
    case 'batch_added':
      await applyBatchAdded(event.payload as BatchAddedPayload);
      return;
    case 'batch_consumed':
      await applyBatchConsumed(event.payload as BatchConsumedPayload);
      return;
    case 'batch_quantity_adjusted':
      await applyBatchQuantityAdjusted(event.payload as BatchQuantityAdjustedPayload);
      return;
    case 'batch_discarded':
      await applyBatchDiscarded(event.payload as BatchDiscardedPayload);
      return;
    case 'batch_opened':
      await applyBatchOpened(event.payload as BatchOpenedPayload);
      return;
    case 'batch_updated':
      await applyBatchUpdated(event.payload as BatchUpdatedPayload);
      return;
    case 'item_discarded':
      await applyItemDiscarded(event.payload as ItemDiscardedPayload);
      return;
    case 'item_min_quantity_set':
      await applyItemMinQuantitySet(event.payload as ItemMinQuantitySetPayload);
      return;
    case 'shopping_added':
      await applyShoppingAdded(event.payload as ShoppingAddedPayload);
      return;
    case 'shopping_status_changed':
      await applyShoppingStatusChanged(event.payload as ShoppingStatusChangedPayload);
      return;
    case 'shopping_received':
      await applyShoppingReceived(event.payload as ShoppingReceivedPayload);
      return;
    case 'settings_updated':
      await applySettingsUpdated(event.payload as SettingsUpdatedPayload);
      return;
    case 'subscription_created':
      await applySubscriptionCreated(event.payload as SubscriptionCreatedPayload);
      return;
    case 'subscription_advanced':
      await applySubscriptionAdvanced(event.payload as SubscriptionAdvancedPayload);
      return;
    case 'subscription_deactivated':
      await applySubscriptionDeactivated(event.payload as SubscriptionDeactivatedPayload);
      return;
    case 'subscription_updated':
      await applySubscriptionUpdated(event.payload as SubscriptionUpdatedPayload);
      return;
    default:
      return;
  }
}

/**
 * Rebuild all inventory projections from the event log (recovery / migration path).
 */
export async function rebuildProjections(): Promise<void> {
  await db.transaction(
    'rw',
    [db.events, db.items, db.shoppingItems, db.subscriptions, db.settings],
    async () => {
      const events = await db.events.orderBy('id').toArray();
      await db.items.clear();
      await db.shoppingItems.clear();
      await db.subscriptions.clear();
      await db.settings.clear();
      for (const event of events) {
        await applyEventToProjections(event);
      }
      // 確保 settings singleton 永遠存在（即使無 settings_updated event）
      if (!(await db.settings.get('singleton'))) {
        await db.settings.put({ ...DEFAULT_USER_SETTINGS });
      }
    },
  );
}

// ─────────────── Per-event appliers ───────────────

async function applyItemCreated(p: ItemCreatedPayload): Promise<void> {
  await db.items.put({
    id: p.itemId,
    name: p.name,
    normalizedName: p.normalizedName,
    category: p.category,
    location: p.location,
    status: 'active',
    batches: [
      {
        id: p.initialBatch.batchId,
        quantity: p.initialBatch.quantity,
        unit: p.initialBatch.unit,
        ...(p.initialBatch.packageSize !== undefined
          ? { packageSize: p.initialBatch.packageSize }
          : {}),
        ...(p.initialBatch.packageUnit !== undefined
          ? { packageUnit: p.initialBatch.packageUnit }
          : {}),
        addedAt: p.initialBatch.addedAt,
        expiresAt: p.initialBatch.expiresAt,
        expirySource: p.initialBatch.expirySource,
        openedAt: null,
      },
    ],
  });
}

async function applyBatchAdded(p: BatchAddedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  item.batches.push({
    id: p.batchId,
    quantity: p.quantity,
    unit: p.unit,
    ...(p.packageSize !== undefined ? { packageSize: p.packageSize } : {}),
    ...(p.packageUnit !== undefined ? { packageUnit: p.packageUnit } : {}),
    addedAt: p.addedAt,
    expiresAt: p.expiresAt,
    expirySource: p.expirySource,
    openedAt: null,
  });
  // 加 batch 進來代表 item 又有貨了：復活 all_consumed / all_expired
  if (item.status === 'all_consumed' || item.status === 'all_expired') {
    item.status = 'active';
  }
  await db.items.put(item);
}

async function applyBatchConsumed(p: BatchConsumedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  const batch = item.batches.find((b) => b.id === p.batchId);
  if (!batch) return;
  batch.quantity = Math.max(0, batch.quantity - p.amount);
  finalizeIfAllConsumed(item);
  await db.items.put(item);
}

async function applyBatchQuantityAdjusted(p: BatchQuantityAdjustedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  const batch = item.batches.find((b) => b.id === p.batchId);
  if (!batch) return;
  batch.quantity = Math.max(0, p.newQuantity);
  finalizeIfAllConsumed(item);
  await db.items.put(item);
}

async function applyBatchDiscarded(p: BatchDiscardedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  item.batches = item.batches.filter((b) => b.id !== p.batchId);
  if (item.batches.length === 0) {
    item.status = 'all_expired';
  }
  await db.items.put(item);
}

async function applyBatchOpened(p: BatchOpenedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  const batch = item.batches.find((b) => b.id === p.batchId);
  if (!batch) return;
  batch.openedAt = p.openedAt;
  await db.items.put(item);
}

async function applyBatchUpdated(p: BatchUpdatedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  const batch = item.batches.find((b) => b.id === p.batchId);
  if (!batch) return;
  if (p.quantity !== undefined) batch.quantity = Math.max(0, p.quantity);
  if (p.unit !== undefined) batch.unit = p.unit;
  if (p.packageSize !== undefined) {
    if (p.packageSize === null || p.packageSize <= 0) delete batch.packageSize;
    else batch.packageSize = p.packageSize;
  }
  if (p.packageUnit !== undefined) {
    if (p.packageUnit === null || p.packageUnit === '') delete batch.packageUnit;
    else batch.packageUnit = p.packageUnit;
  }
  if (p.expiresAt !== undefined) batch.expiresAt = p.expiresAt;
  if (p.expirySource !== undefined) batch.expirySource = p.expirySource;
  finalizeIfAllConsumed(item);
  await db.items.put(item);
}

async function applyItemDiscarded(p: ItemDiscardedPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  item.status = 'discarded';
  await db.items.put(item);
}

async function applyItemMinQuantitySet(p: ItemMinQuantitySetPayload): Promise<void> {
  const item = await db.items.get(p.itemId);
  if (!item) return;
  if (p.minQuantity > 0) {
    item.minQuantity = p.minQuantity;
  } else {
    delete item.minQuantity;
  }
  await db.items.put(item);
}

async function applyShoppingAdded(p: ShoppingAddedPayload): Promise<void> {
  const row: ShoppingItem = {
    id: p.shoppingItemId,
    name: p.name,
    reason: p.reason,
    status: 'planned',
    addedAt: p.addedAt,
  };
  if (p.quantity !== undefined) row.quantity = p.quantity;
  if (p.unit !== undefined) row.unit = p.unit;
  if (p.source !== undefined) row.source = p.source;
  if (p.sourceName !== undefined) row.sourceName = p.sourceName;
  if (p.expectedArrival !== undefined) row.expectedArrival = p.expectedArrival;
  if (p.unitPrice !== undefined) row.unitPrice = p.unitPrice;
  if (p.packageSize !== undefined) row.packageSize = p.packageSize;
  if (p.packageContentUnit !== undefined) row.packageContentUnit = p.packageContentUnit;
  if (p.subscriptionId !== undefined) row.subscriptionId = p.subscriptionId;
  await db.shoppingItems.put(row);
}

async function applyShoppingStatusChanged(p: ShoppingStatusChangedPayload): Promise<void> {
  const item = await db.shoppingItems.get(p.shoppingItemId);
  if (!item) return;
  item.status = p.newStatus;
  // ordered / in_transit 都帶 source / sourceName / expectedArrival 投影；
  // in_transit 額外有 inTransitAt（實際出貨時間）。
  if (p.newStatus === 'ordered' || p.newStatus === 'in_transit') {
    if (p.orderedAt !== undefined) item.orderedAt = p.orderedAt;
    if (p.inTransitAt !== undefined) item.inTransitAt = p.inTransitAt;
    if (p.source !== undefined) item.source = p.source;
    if (p.sourceName !== undefined) item.sourceName = p.sourceName;
    if (p.expectedArrival !== undefined) item.expectedArrival = p.expectedArrival;
  }
  if (p.unitPrice !== undefined) item.unitPrice = p.unitPrice;
  if (p.packageSize !== undefined) item.packageSize = p.packageSize;
  if (p.packageContentUnit !== undefined) item.packageContentUnit = p.packageContentUnit;
  await db.shoppingItems.put(item);
}

async function applyShoppingReceived(p: ShoppingReceivedPayload): Promise<void> {
  // 1) Mark shopping item as received
  const shoppingItem = await db.shoppingItems.get(p.shoppingItemId);
  if (shoppingItem) {
    shoppingItem.status = 'received';
    shoppingItem.receivedAt = p.receivedAt;
    if (p.unitPrice !== undefined) shoppingItem.unitPrice = p.unitPrice;
    if (p.packageSize !== undefined) shoppingItem.packageSize = p.packageSize;
    if (p.packageContentUnit !== undefined)
      shoppingItem.packageContentUnit = p.packageContentUnit;
    if (p.sourceName !== undefined) shoppingItem.sourceName = p.sourceName;
    await db.shoppingItems.put(shoppingItem);
  }
  // 2) Side effect: create / append batch (兩階段入庫的入庫端)
  const newBatch = {
    id: p.batchId,
    quantity: p.quantity,
    unit: p.unit,
    ...(p.batchPackageSize !== undefined ? { packageSize: p.batchPackageSize } : {}),
    ...(p.batchPackageUnit !== undefined ? { packageUnit: p.batchPackageUnit } : {}),
    addedAt: p.receivedAt,
    expiresAt: p.expiresAt,
    expirySource: p.expirySource,
    openedAt: null,
  };
  if (p.isNewItem) {
    await db.items.put({
      id: p.itemId,
      name: p.name,
      normalizedName: p.normalizedName,
      category: p.category,
      location: p.location,
      status: 'active',
      batches: [newBatch],
    });
  } else {
    const item = await db.items.get(p.itemId);
    if (!item) return;
    item.batches.push(newBatch);
    if (item.status === 'all_consumed' || item.status === 'all_expired') {
      item.status = 'active';
    }
    await db.items.put(item);
  }
}

function finalizeIfAllConsumed(item: Item): void {
  const total = item.batches.reduce((sum, b) => sum + b.quantity, 0);
  if (total <= 0) {
    item.status = 'all_consumed';
  }
}

async function applySubscriptionCreated(p: SubscriptionCreatedPayload): Promise<void> {
  const row: Subscription = {
    id: p.subscriptionId,
    knowledgeId: p.knowledgeId,
    defaultName: p.defaultName,
    frequency: p.frequency,
    nextDelivery: p.nextDelivery,
    source: p.source,
    active: true,
    defaultQuantity: p.defaultQuantity,
    defaultUnit: p.defaultUnit,
    leadDays: p.leadDays,
  };
  if (p.customDays !== undefined) row.customDays = p.customDays;
  await db.subscriptions.put(row);
}

async function applySubscriptionAdvanced(p: SubscriptionAdvancedPayload): Promise<void> {
  const sub = await db.subscriptions.get(p.subscriptionId);
  if (!sub) return;
  sub.nextDelivery = p.newNextDelivery;
  await db.subscriptions.put(sub);
}

async function applySubscriptionDeactivated(p: SubscriptionDeactivatedPayload): Promise<void> {
  const sub = await db.subscriptions.get(p.subscriptionId);
  if (!sub) return;
  sub.active = false;
  await db.subscriptions.put(sub);
}

async function applySettingsUpdated(p: SettingsUpdatedPayload): Promise<void> {
  const current = (await db.settings.get('singleton')) ?? { ...DEFAULT_USER_SETTINGS };
  // 淺合併 patch；UserSettings 沒巢狀物件，array 整個取代
  const next: UserSettings = { ...current, ...(p.patch as Partial<UserSettings>), id: 'singleton' };
  // 顯式 undefined 視為「清除欄位」（用於 activeShoppingSource 等 optional 欄位的清除）
  for (const [key, value] of Object.entries(p.patch)) {
    if (value === undefined) {
      delete (next as unknown as Record<string, unknown>)[key];
    }
  }
  await db.settings.put(next);
}

async function applySubscriptionUpdated(p: SubscriptionUpdatedPayload): Promise<void> {
  const sub = await db.subscriptions.get(p.subscriptionId);
  if (!sub) return; // 找不到則 no-op
  sub.knowledgeId = p.knowledgeId;
  sub.defaultName = p.defaultName;
  sub.frequency = p.frequency;
  if (p.customDays !== undefined) {
    sub.customDays = p.customDays;
  } else {
    delete sub.customDays;
  }
  sub.nextDelivery = p.nextDelivery;
  sub.source = p.source;
  sub.defaultQuantity = p.defaultQuantity;
  sub.defaultUnit = p.defaultUnit;
  sub.leadDays = p.leadDays;
  // active 與 createdAt 不動
  await db.subscriptions.put(sub);
}
